import Layout from '@components/layout/layout-six'
import ProductSingleDetails from '@components/product/product'
import Container from '@components/ui/container'
import Divider from '@components/ui/divider'
import * as magento from '@framework/magento'
import { convertProduct } from '@framework/utils/convert-product'
import ga4Services from '@services/ga4services'
import { formatProductName } from '@utils/format-product-name'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useEffect, useState } from 'react'
import * as typesense from '../../framework/basic-rest/typesense'
import Script from 'next/script'
import { useTranslation } from 'next-i18next'
import Seo from '@components/seo/seo'
import { siteSettings } from '@settings/site-settings'
import { useRouter } from 'next/router'
import {
  getProductIdFromUrl,
  getProductSeoDescription,
  getProductSeoTitle,
} from '@utils/helper'
import {
  resolveCmsPageData,
  resolveProductPageData,
} from 'lib/resolve-page-data'
import CmsPage from '@components/cms-page'
import { META_DESCRIPTION, META_TITLE } from '@constants/index'

export default function ProductPDP({
  product: initialProduct,
  bread_path,
  locale,
  cmsData,
}: any) {
  const { t: translate } = useTranslation(['common', 'categories'])
  const [product, setProduct] = useState(initialProduct)
  const router = useRouter()
  const metaTitle = product?.mag_attributes?.find(
    (attr: any) => attr.attribute_code === META_TITLE,
  )?.value

  const metaDescription = product?.mag_attributes?.find(
    (attr: any) => attr.attribute_code === META_DESCRIPTION,
  )?.value

  useEffect(() => {
    const fetchUpdatedProduct = async () => {
      const productPdp = router.query.product_pdp

      if (typeof productPdp === 'string') {
        const pid = getProductIdFromUrl(productPdp)

        if (pid) {
          await getUpdatedProduct(pid)
        }
      } else {
        console.error('Invalid product_pdp query parameter:', productPdp)
      }
    }

    fetchUpdatedProduct()
  }, [router.query.product_pdp])

  let date = new Date()
  date.setDate(date.getDate() + 30)
  const schema_pdp = {
    '@context': 'http://schema.org',
    '@type': 'Product',
    name: product?.name,
    description: product?.description,
    image: convertProduct(product)?.gallery
      ? convertProduct(product)?.gallery[0]?.original
      : product?.image?.original,
    url: siteSettings.base_url + product?.slug,
    sku: product?.sku,
    manufacturer: product?.manufacturer,
    mpn: product?.mpn,
    gtin: product?.gtin,
    brand: {
      '@type': 'Thing',
      name: product?.brand,
    },
    offers: {
      '@type': 'Offer',
      availability: 'http://schema.org/InStock',
      itemCondition: 'http://schema.org/NewCondition',
      price: product?.price,
      priceCurrency: process.env.NEXT_PUBLIC_CURRENCY_CODE,
      priceValidUntil: `${date.getFullYear()}-${
        date.getMonth() + 1
      }-${date.getDate()}`,
      url: `${process.env.NEXT_PUBLIC_WEBSITE_URL}/` + product?.slug,
    },
  }
  let formattedName = formatProductName(
    product?.name,
    product?.brand,
    product?.attributes?.case_description,
    product?.case_quantity,
  )
  useEffect(() => {
    ga4Services.viewProduct([
      {
        id: product?.id,
        formattedName: formattedName,
        category: product?.category_l1,
        category_l2: product?.category_l2,
        category_l3: product?.category_l3,
        category_l4: product?.category_l4,
        brand: product?.brand,
        price: product?.price,
      },
    ])
  }, [])

  const defaultImage = {
    url: `/assets/images/foodservicedirect_snap_${locale}.png`,
    width: 858,
    height: 407,
    alt: siteSettings.logo.alt,
  }

  const images = product?.gallery?.length
    ? [
        {
          url: product.gallery[0].original,
          width: 1000,
          height: 1000,
          alt: product.title || `${product.brand} ${product.name}`,
        },
      ]
    : [defaultImage]

  const getUpdatedProduct = async (id: number) => {
    try {
      const response = await typesense.getProduct(id, locale)
      setProduct(response)
    } catch (error) {
      console.error('Failed to fetch product:', error)
    }
  }
  const renderProductDetails = () => {
    return (
      <>
        {product && (
          <Seo
            title={getProductSeoTitle(product, metaTitle)}
            description={getProductSeoDescription(
              product,
              translate,
              metaDescription,
            )}
            path={
              locale !== siteSettings.defaultLanguage
                ? `${locale}/${product.slug}`
                : product.slug
            }
            images={images}
            canonical={
              locale !== siteSettings.defaultLanguage
                ? `${locale}/${product.slug}`
                : product.slug
            }
          />
        )}
        {product && (
          <Script
            id={product.sku}
            key="schema_product"
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schema_pdp) }}
          ></Script>
        )}
        <Divider />
        <div>
          <Container clean={true}>
            {/* <Breadcrumb pathname={`/${bread_path}`} /> */}
            {/* @ts-ignore */}
            <ProductSingleDetails
              getUpdatedProduct={getUpdatedProduct}
              product={product}
              initialProduct={initialProduct}
              pathname={`/${bread_path}`}
            />
          </Container>
        </div>
      </>
    )
  }

  const renderCmsData = () => {
    return <CmsPage {...cmsData} />
  }
  return (
    <>
      {cmsData && Object.keys(cmsData).length > 0 ? (
        <>{renderCmsData()}</>
      ) : (
        renderProductDetails()
      )}
    </>
  )
}

ProductPDP.Layout = Layout

export async function getServerSideProps(context: any) {
  context.res.setHeader(
    'Cache-Control',
    `s-maxage=${60 * 60}, stale-while-revalidate=${24 * 60 * 60}`,
  )

  const slug = context.params?.product_pdp || ''
  const locale = context.locale ?? 'en'

  try {
    const cmsResult = await resolveCmsPageData(slug, locale)

    if (cmsResult) {
      return {
        props: {
          ...(await serverSideTranslations(locale, [
            'common',
            'categories',
            'home',
            'forms',
            'menu',
            'footer',
            'landing',
            'promise',
          ])),
          ...cmsResult.props,
        },
      }
    }

    const productResult: any = await resolveProductPageData(slug, locale)

    if (!productResult?.props?.product?.is_active) return { notFound: true }

    if (productResult) {
      return {
        props: {
          ...(await serverSideTranslations(locale, [
            'categories',
            'common',
            'forms',
            'menu',
            'footer',
            'savelist',
            'checkout',
            'pdp',
            'rebate',
          ])),
          ...productResult.props,
        },
      }
    }

    return { notFound: true }
  } catch (err) {
    console.error('Error resolving data:', err)
    return { notFound: true }
  }
}
